import { matchPath, generatePath } from 'react-router-dom'

/**
 * The field app's routes, and how they map to their desktop equivalents.
 *
 * The field app is not "mobile `/locations`" — it is a different set of screens
 * with a different navigation shape, so it gets its own namespace rather than
 * swapping components underneath the existing URLs.
 *
 * That choice creates one obligation: a link must survive being sent from a
 * phone to a colleague at a desk, and the other way round. Hence an explicit
 * two-way map rather than hoping it never comes up. Mirrors the shape of
 * `constants/desktopOnlyRoutes.ts` deliberately — same idea, same file layout.
 */

const FIELD_ROOT = '/field'

export const fieldRoutes = {
	HOME: FIELD_ROOT,
	/** Proximity view. Was the home until the card home took that place. */
	NEAR: `${FIELD_ROOT}/near`,
	NEW_SITE: `${FIELD_ROOT}/new`,
	VISIT: `${FIELD_ROOT}/visit/:siteId`,
	TASKS: `${FIELD_ROOT}/tasks`,
	PROJECTS: `${FIELD_ROOT}/projects`,
	PARTNERS: `${FIELD_ROOT}/partners`,
	IMAGES: `${FIELD_ROOT}/images`,
	PROJECT: `${FIELD_ROOT}/project/:projectId`,
	SITE: `${FIELD_ROOT}/site/:siteId`,
	LOCATION: `${FIELD_ROOT}/location/:locationId`,
	SCORE: `${FIELD_ROOT}/score/:siteId`,
	CAPTURE: `${FIELD_ROOT}/capture`,
} as const

/** Everything under `/field`, for "is the user in the field app" checks. */
export const isFieldPath = (pathname: string): boolean =>
	pathname === FIELD_ROOT || pathname.startsWith(`${FIELD_ROOT}/`)

/**
 * Field route ↔ desktop route, for the pairs that have a counterpart.
 *
 * Not every field screen has one. A Visit is a field-only idea with no desktop
 * screen to fall back to, so it maps to the site it is about — the closest
 * thing to "what this link was really showing you".
 */
const EQUIVALENTS: { field: string; desktop: string }[] = [
	{ field: fieldRoutes.SITE, desktop: '/evaluation-sites/:siteId' },
	{ field: fieldRoutes.LOCATION, desktop: '/locations/:locationId' },
	{ field: fieldRoutes.PROJECT, desktop: '/projects/:projectId' },
	{ field: fieldRoutes.VISIT, desktop: '/evaluation-sites/:siteId' },
	{ field: fieldRoutes.SCORE, desktop: '/evaluation-sites/:siteId' },
	{ field: fieldRoutes.PARTNERS, desktop: '/partners' },
	{ field: fieldRoutes.HOME, desktop: '/locations' },
]

/** Swap a pathname between the two namespaces, keeping its params. */
const translate = (pathname: string, from: 'field' | 'desktop'): string | null => {
	const to = from === 'field' ? 'desktop' : 'field'
	for (const pair of EQUIVALENTS) {
		const match = matchPath(pair[from], pathname)
		if (!match) continue
		try {
			return generatePath(pair[to], match.params as Record<string, string>)
		} catch {
			// A pattern needing a param the other side does not carry. Better to
			// fall through to null and leave the user where they are than to
			// navigate them somewhere broken.
			return null
		}
	}
	return null
}

/**
 * The desktop URL showing the same thing, or null if there is no equivalent.
 *
 * Used when a field link is opened on a desktop: send them to the real screen
 * rather than rendering phone chrome on a large display.
 */
export const desktopEquivalentOf = (pathname: string): string | null =>
	translate(pathname, 'field')

/**
 * The field URL showing the same thing, or null.
 *
 * Used to OFFER field mode on a phone — never to force it. A forced redirect
 * breaks a link somebody deliberately shared, and there are good reasons to
 * open the desktop screen on a phone, such as reading something the field app
 * does not show.
 */
export const fieldEquivalentOf = (pathname: string): string | null =>
	translate(pathname, 'desktop')
